<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>

        <div
            v-if="initialLoading"
            key="loadingList"
            class="animate__animated animate__fadeIn animate_fadeOut animate__fast bg-gray-50 rounded-lg border d-flex justify-content-center align-items-center"
        >
            <transition name="fade-fast" mode="out-in">
                <b-skeleton-table 
                    v-if="initialLoading || loading"
                    key="loading"
                    :rows="perPage" 
                    :columns="fields.length" 
                    show-footer 
                    :table-props="{bordered: true}"
                ></b-skeleton-table>
                <div 
                    v-else
                    key="empty"
                    class="text-center"
                >
                    <br>
                    <span class="text-muted font-size-100">
                        No Quality Issues
                    </span>
                    
                </div>
            </transition>
        </div>

        <b-card 
            v-else
            key="notEmptyList"
            class="shadow rounded-lg animate__animated animate__fadeIn animate__fast"
            no-body
        >

            <transition name="fade-in-fast" mode="out-in">

                <div
                    v-if="!totalRows"
                    key="emptyList"
                    class="d-flex justify-content-center align-items-center"
                    style="min-height: 200px;"
                >
                    <div class="text-center text-muted font-size-100">
                        No Quality Issues
                    </div>
                </div>

                <div v-else key="notEmpty">

                    <b-table 
                        id="tu-active-tasks-list"
                        :items="items"
                        :fields="fieldsToShow"
                        :busy="loading"
                        primary-key="id" 
                        responsive
                        no-local-sorting
                        no-sort-reset
                        :sort-by.sync="sortByField"
                        :sort-desc.sync="sortDesc"
                        @sort-changed="refreshList"
                        hover
                        tbody-class="cells-align-middle"
                        class="no-top-border td-align-middle mb-0"
                        thead-class="overflow-hidden cells-align-middle"
                        head-variant=""
                    >
                        <template #cell(title)="data">
                            <b-link :href="data.item.url" target="_blank">{{ data.value }}</b-link>
                            <!-- <b-link :href="getListUrl(data.item)">{{ data.value }}</b-link>
                            <b-badge 
                                v-if="data.item.is_waiting_on_tu" 
                                variant="soft-danger" 
                                v-b-tooltip.hover
                                title="Waiting on your action"
                                class="ml-2 text-uppercase"
                            >You</b-badge> -->
                        </template>
                        <template #cell(status)="data">
                            <status-object-badge
                                :status-object="data.item.status_object" 
                                text-uppercase
                                font-size="0.8rem"
                                title-field="title"
                                value-field="value"
                                no-wrap
                            ></status-object-badge>
                        </template>
                        <!-- <template #cell(deadline)="data">
                            <date-time-deadline
                                :date="data.value" 
                                empty-text="Not Set"
                                date-class="d-none"
                            ></date-time-deadline>
                        </template> -->
                    </b-table>
                </div>
            </transition>

            <template #footer v-if="totalRows">
                <base-table-pagination
                    :current-page.sync="currentPage"
                    :per-page.sync="perPage"
                    :total-items="totalRows"
                    :show-per-page="showPerPage"
                    item-name="Issues"
                    @update:perPage="refreshList"
                    @update:currentPage="getList"
                ></base-table-pagination>
            </template>
        </b-card>

        <!-- <pre>{{ items }}</pre> -->
    </div>
</template>

<script>
//import { errorHandling } from '../../js/custom-vue-mixins';
import { errorHandling, currentUserInfoMixin, splTableDefaults } from '@/js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service';
import BaseTablePagination from '../BaseTablePagination.vue';
//import DateTimeDeadline from '../table-cell-comps/DateTimeDeadline.vue';
import { 
    FormatDateHelper, 
    //FormatDateTimeLocal, 
    //HumanizeDateTimeHelper, 
    //DateApproachingDeadline 
} from '../../js/filters-helpers';

export default {
    name: 'MyQualityIssues',
    components: {
        BaseTablePagination,
        //DateTimeDeadline,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        initialSortField: {  // FIXED IN BACKEND FOR NOW - initial field for sortBy, does not have to be in showFields array, but must be in fields array and must be in the ordering options for API endpoint
            type: String,
            default: 'modified',
        },
        initialSortDesc: {  // initial sort in descending order?
            type: Boolean,
            default: true,
        },
        fixedPerPage: {  // if you want to force a fixed # per page AND HIDE PER-PAGE SELECTOR
            // this is useful for dashboard views where you want to control size of grid
            // validator... we use same one in base-table-pagination component, so using here too
            type: [String, Number],
            required: false,
            validator: function (value) {
                return splTableDefaults.perPageOptions.includes(value);
                //return defaultPerPageOptions.includes(value);
            },
        },
        hideFields: {  // aray of strings of fields to HIDE, only use the limited fields we have here...
            type: Array,
            required: false,
        },
    },
    data() {
        return {
            errorMsg: 'Error',
            error: false,
            initialLoading: true,  // set to false after getting list the first time
            loading: false,
            items: [],  // the list from API
            totalRows: null,
            currentPage: 1,  // will get sync'd by base-table-pagination
            perPage: 25,  // will get sync'd by base-table-pagination, and also set by our mixin default value during create hook
            showPerPage: true,  // if we set fixedPerPage prop, this is set false in created() - and passed to same prop in pagination component
            // for sorting:
            sortByField: 'modified',  // FIXED IN BACKEND FOR NOW default gets set via prop... sortByField and sortDesc will get .sync'd by b-table, then gets used in API call query params
            sortDesc: true,  // sort descending = true (adds '-' sign in backend), sort ascending = false (no minus sign)
            fields: [
                // for column widths, do not set fixed or responsive, then use class: on field def object with either min-width or max-width (but not width directly)
                // NOTE - this was originally made to show a merged list of DemoWorkflowRun AND QualityIssue inst
                // so fields do not map directly to a model - but to a generic common serializer 
                {
                    key: 'title',
                    label: 'Issue',
                    sortable: false,
                    //api_ordering_name: 'title',  // set this for any sortable field, this is what gets sent to API for ordering=_____
                    tdClass: 'font-weight-bold'
                },{
                    key: 'issue_type',
                    label: 'Issue Type',
                    sortable: false,
                    //api_ordering_name: 'workflow__workflow_spec__name',
                    class: '',  // max-width-80
                },{
                    key: 'status',
                    label: 'Status',
                    sortable: false,
                    //api_ordering_name: 'workflow__title',
                    class: '',  // max-width-80
                },{
                    key: 'created',
                    label: 'Date Created',
                    sortable: false,
                    //api_ordering_name: 'created',
                    class: 'text-center',
                    formatter: (value) => FormatDateHelper(value),
                },{
                    key: 'modified',
                    label: 'Last Modified',
                    sortable: false,
                    //api_ordering_name: 'modified',
                    class: 'text-center',
                    formatter: (value) => FormatDateHelper(value),
                },
            ],
        }
    },
    computed: {
        /* skeletonRows() {
            // the number of rows to show when loading
            // you might have perPage of 25, but likely only will show ~10... so make it closer to that
            // if you have fixedPerPage -- use that 
            return this.perPage;
        }, */
        fieldsToShow() {
            // return array of field def objects
            const hideFieldNames = this.hideFields && Array.isArray(this.hideFields) ? [...this.hideFields] : [];
            return this.fields.filter(fldObj => !hideFieldNames.includes(fldObj.key));
        },
    },
    created() {
        // merge any presetFilters (object from prop) with this.defaultFilterValues to make this.initialFilterValues
        /* if (this.presetFilters && !isEmpty(this.presetFilters)) {
            // target is empty object so you don't mutate originals, merge presetFilters last
            this.initialFilterValues = Object.assign({}, this.defaultFilterValues, this.presetFilters);  
        } else {
            // if we DO NOT have any presetFilters, we need to just do same thing, but without presetFilters... maybe there's a shorter way, but this works
            this.initialFilterValues = Object.assign({}, this.defaultFilterValues);
        }
        this.resetFilterValues(); */
        // FIGURE OUT THE perPage to use
        if (this.fixedPerPage) {
            this.perPage = Number(this.fixedPerPage);
            this.showPerPage = false;
        } else {
            this.perPage = splTableDefaults.perPage;  // splTableDefaults is from import
            this.showPerPage = true;
        }
        this.sortByField = this.initialSortField;  // set initial sort field based on prop
        this.sortDesc = this.initialSortDesc;  // set initial sort direction based on prop
    },
    mounted() {
        this.getList();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        refreshList() {
            // MAY BE CALLED BY PARENT USING $refs
            this.currentPage = 1;
            this.$nextTick(() => this.getList());
        },
        async getList() {
            this.loading = true;

            // BUILD QUERY PARAMS
            // axiosConfig['params'] is where we put search/filter params
            let filterParams = {  // grab the current search/filter values
                // search: this.currentFilterValues.searchFilter || null,
                // grouping: this.currentFilterValues.grouping, 
                // rfx_type: this.currentFilterValues.rfxTypeFilter || null,
                // sm_state: this.currentFilterValues.stateFilter || null,
                // sourcing_category: this.currentFilterValues.categoryFilter,
            };
            let orderingParams = {
                ordering: this.getOrderingFieldString(),
            };
            let paginationParams = {
                page: this.currentPage,
                page_size: this.perPage,
            };
            let combinedParams = Object.assign({}, filterParams, orderingParams, paginationParams);
            let axiosConfig = {params: combinedParams};

            let response;
            try {
                response = await suppliosApi.dashboard.tu_active_quality_issues(axiosConfig);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (response?.data) {
                this.items = response.data.results;  // paginated!
                this.totalRows = response.data.count;
            }
            this.loading = false;
            this.initialLoading = false;
        },
        getOrderingFieldString: function() {
            // look up what field name (string) we should use to send to API, in case key here is different than what API should order by
            // useful if you have serializer methods that get values from related objects... you can use double-underscore notation in DRF for ordering
            // also add '-' if sortDesc is true
            // LATER let apiOrderingName = this.fields.find(fld => fld.key === this.sortByField).api_ordering_name;
            // LATER return (this.sortDesc ? '-' : '') + apiOrderingName;
            return '-modified';
        },
    }
}
</script>

<style lang="scss" scoped>

table.b-table[aria-busy='true'] {
    opacity: 0.5;
}

</style>